import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Package, ShieldCheck, Truck, FileText, Book } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const BUSINESS_TYPES = ["Restaurant", "Hotel", "Café", "Bakery", "Catering", "Other"];
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

const INCLUDED = [
  { name: "Karpatka Mini Cake", slug: "karpatka" },
  { name: "Tiramisu Mini Cake", slug: "tiramisu" },
  { name: "NY Cheesecake Mini Cake", slug: "new-york-cheesecake" },
  { name: "Chajá Mini Cake", slug: "chaja-cake" },
  { name: "Chocolate Mini Cake", slug: "chocolate-cake" },
];

const MATERIALS = [
  "Product Catalog",
  "Wholesale pricing information",
  "Suggested menu pricing",
  "Ordering and logistics guide",
];

const IDEAL_FOR = [
  "Restaurants",
  "Boutique hotels",
  "Luxury hotels",
  "Cafés and bakeries",
  "Catering companies",
  "Hospitality groups",
];

export default function SampleBox() {
  const [settings, setSettings] = useState({ sample_box_fee: "", sample_box_fee_note: "", sample_box_lead_time: "" });
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    business_type: "",
    city: "",
    state: "",
    zip_code: "",
    email: "",
    phone: "",
    offers_desserts: "",
    monthly_sales: "",
    locations: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/sample-box", form);
      setSuccess(true);
      toast.success("Sample box request received — we'll review and follow up shortly.");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="sample-box-success" className="max-w-3xl mx-auto px-6 py-24">
        <div className="bg-white border border-brand-line rounded-[2rem] shadow-soft-lg p-10 md:p-16 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-brand-red grid place-items-center">
            <CheckCircle2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl text-brand-ink">Request received</h1>
          <p className="mt-4 text-brand-stone font-ui max-w-md mx-auto">
            Thank you — our team will review your business details and follow up within one business day with next steps for your sample box.
          </p>
          <div className="mt-4 text-xs tracking-[0.25em] uppercase text-brand-pink font-ui font-bold">
            Every occasion needs a sweet touch
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/catalog" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white text-sm font-ui font-semibold hover:bg-brand-redDark transition">
              Browse the collection
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-brand-ink text-brand-ink text-sm font-ui font-semibold hover:bg-brand-ink hover:text-white transition">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sample-box-page">
      <SEO
        title="Sample Box — Try before you buy"
        description="Request a curated tasting box with our signature wholesale desserts. Sample the quality before committing to a program."
      />
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-16">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-brand-red">
              <span className="w-8 h-px bg-brand-red" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-ui font-bold">Sample Box · B2B only</span>
            </div>
            <h1 className="mt-6 font-display text-[3rem] sm:text-[4.25rem] lg:text-[5rem] leading-[1] tracking-tight text-brand-ink">
              Experience Cake World<br/>
              <span className="italic text-brand-red">before you order.</span>
            </h1>
            <p className="mt-8 text-lg text-brand-stone max-w-xl leading-relaxed font-ui">
              Request a curated sample box and discover why restaurants, hotels and cafés choose Cake World for their dessert programs.
            </p>
            <a href="#request-form" data-testid="hero-request-sample" className="mt-10 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-brand-ink text-white text-base font-ui font-semibold hover:bg-brand-red active:scale-95 transition">
              Request your sample box <ArrowRight className="w-5 h-5" />
            </a>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-[2rem] overflow-hidden aspect-square shadow-soft-lg bg-white grid place-items-center p-4">
              <img
                src="https://customer-assets.emergentagent.com/job_bakery-b2b/artifacts/du6yg2yy_ChatGPT%20Image%2011%20mar%202026%2C%2002_24_24%20p.m..png"
                alt="Cake World sample box with five mini cakes"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="dot-divider max-w-7xl mx-auto" />

      {/* WHAT'S INSIDE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="max-w-2xl">
          <div className="eyebrow">What's inside</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-brand-ink leading-tight">Curated for hospitality evaluation.</h2>
          <p className="mt-5 text-brand-stone font-ui">
            Each Cake World sample box is designed to help hospitality businesses evaluate our desserts for their menu — a tasting of our five signatures plus everything you need to make a purchase decision.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {/* Mini cakes */}
          <div className="bg-white border border-brand-line rounded-3xl p-8 shadow-soft">
            <div className="w-12 h-12 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center"><Package className="w-5 h-5" /></div>
            <h3 className="mt-5 font-display text-2xl text-brand-ink">Typical sample box includes</h3>
            <ul className="mt-5 space-y-3">
              {INCLUDED.map((i) => (
                <li key={i.slug} data-testid={`sample-item-${i.slug}`} className="flex items-center gap-3 text-sm font-ui text-brand-ink">
                  <div className="w-6 h-6 rounded-full bg-brand-red text-white grid place-items-center text-[10px] font-bold">✓</div>
                  {i.name}
                </li>
              ))}
            </ul>
          </div>

          {/* Additional materials */}
          <div className="bg-brand-pinkSoft border border-brand-line rounded-3xl p-8">
            <div className="w-12 h-12 rounded-full bg-white text-brand-red grid place-items-center"><FileText className="w-5 h-5" /></div>
            <h3 className="mt-5 font-display text-2xl text-brand-ink">Additional materials</h3>
            <ul className="mt-5 space-y-3">
              {MATERIALS.map((m) => (
                <li key={m} className="flex items-center gap-3 text-sm font-ui text-brand-ink">
                  <Book className="w-4 h-4 text-brand-red" /> {m}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-brand-ink text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className="eyebrow text-brand-pink">How the sample process works</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-tight">From request to first tasting.</h2>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {[
              { n: "01", title: "Submit your request", body: "Tell us about your business and location.", icon: FileText },
              { n: "02", title: "Qualification", body: "Our team confirms your business eligibility for the B2B program.", icon: ShieldCheck },
              { n: "03", title: "Receive your sample box", body: "A curated dessert selection will be delivered for evaluation.", icon: Truck },
            ].map((s) => (
              <div key={s.n} data-testid={`sample-step-${s.n}`} className="bg-white/5 border border-white/10 rounded-3xl p-8">
                <div className="font-display text-6xl text-brand-pink leading-none">{s.n}</div>
                <div className="mt-4 flex items-center gap-2 text-brand-pink"><s.icon className="w-4 h-4" /><span className="text-[10px] tracking-[0.24em] uppercase font-ui font-bold">Step</span></div>
                <h3 className="mt-3 font-display text-2xl">{s.title}</h3>
                <p className="mt-2 text-sm text-white/70 font-ui">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 max-w-3xl mx-auto text-center bg-white/5 border border-white/10 rounded-3xl p-8" data-testid="sample-box-fee-card">
            <div className="eyebrow text-brand-pink">Sample box fee</div>
            {settings.sample_box_fee && (
              <div className="mt-3 font-display text-5xl text-white">{settings.sample_box_fee}</div>
            )}
            <p className="mt-3 text-white/85 font-ui">{settings.sample_box_fee_note || "Refundable — credited toward your first wholesale order."}</p>
            {settings.sample_box_lead_time && (
              <p className="mt-2 text-white/60 text-sm font-ui">Delivered in {settings.sample_box_lead_time}</p>
            )}
          </div>
        </div>
      </section>

      {/* IDEAL FOR */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="eyebrow">Designed for hospitality professionals</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-brand-ink leading-tight">Reserved for verified foodservice businesses.</h2>
            <p className="mt-5 text-brand-stone font-ui">
              The Cake World sample box is available for hospitality operations only. Consumer requests, one-time events and personal orders are not eligible.
            </p>
          </div>
          <ul className="grid grid-cols-2 gap-3 text-sm font-ui text-brand-ink">
            {IDEAL_FOR.map((x) => (
              <li key={x} className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-brand-pinkSoft border border-brand-line">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-red" /> {x}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FORM */}
      <section id="request-form" className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        <div className="bg-white border border-brand-line rounded-[2rem] shadow-soft-lg p-8 md:p-12">
          <div className="eyebrow">Request form</div>
          <h2 className="mt-3 font-display text-4xl text-brand-ink leading-tight">Request your Cake World sample box.</h2>

          <form onSubmit={submit} className="mt-10 space-y-8" data-testid="sample-box-form">
            <div>
              <h3 className="font-display text-xl text-brand-ink mb-4">Business information</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Business name" required value={form.business_name} onChange={set("business_name")} testId="sb-business" />
                <Field label="Contact name" required value={form.contact_name} onChange={set("contact_name")} testId="sb-contact" />
                <Select label="Business type" required value={form.business_type} onChange={set("business_type")} testId="sb-type" options={BUSINESS_TYPES} />
                <Field label="City" required value={form.city} onChange={set("city")} testId="sb-city" />
                <Select label="State" required value={form.state} onChange={set("state")} testId="sb-state" options={US_STATES} />
                <Field label="Zip code" value={form.zip_code} onChange={set("zip_code")} testId="sb-zip" />
                <Field label="Email" type="email" required value={form.email} onChange={set("email")} testId="sb-email" />
                <Field label="Phone" value={form.phone} onChange={set("phone")} testId="sb-phone" />
              </div>
            </div>

            <div>
              <h3 className="font-display text-xl text-brand-ink mb-4">A few more details</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">Do you currently offer desserts?</label>
                  <div className="mt-2 flex gap-3">
                    {["Yes", "No"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, offers_desserts: opt }))}
                        data-testid={`sb-offers-${opt.toLowerCase()}`}
                        className={`flex-1 h-12 rounded-xl border text-sm font-ui font-semibold transition ${
                          form.offers_desserts === opt
                            ? "bg-brand-ink text-white border-brand-ink"
                            : "bg-white text-brand-ink border-brand-line hover:border-brand-ink"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label="Average monthly dessert sales (optional)" value={form.monthly_sales} onChange={set("monthly_sales")} testId="sb-sales" placeholder="e.g. $2,500" />
                <Field label="Estimated number of locations" value={form.locations} onChange={set("locations")} testId="sb-locations" placeholder="e.g. 3" />
              </div>
              <div className="mt-5">
                <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">Optional message</label>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  data-testid="sb-message"
                  rows={4}
                  placeholder="Anything else we should know about your operation?"
                  className="mt-1.5 w-full rounded-xl bg-white border border-brand-line px-3 py-2.5 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-sample-box"
              className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-brand-red text-white text-base font-ui font-semibold hover:bg-brand-redDark disabled:opacity-50 transition active:scale-[0.98]"
            >
              {submitting ? "Submitting…" : "Request Sample Box"} <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-xs text-brand-stone text-center font-ui">
              Sample boxes are reserved for verified foodservice businesses.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">
        {label} {required && <span className="text-brand-red">*</span>}
      </label>
      <input
        type={type} value={value} onChange={onChange} required={required} placeholder={placeholder}
        data-testid={testId}
        className="mt-1.5 w-full h-12 rounded-xl bg-white border border-brand-line px-3 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red placeholder:text-brand-stoneLight"
      />
    </div>
  );
}

function Select({ label, required, value, onChange, testId, options }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">
        {label} {required && <span className="text-brand-red">*</span>}
      </label>
      <select value={value} onChange={onChange} required={required} data-testid={testId}
        className="mt-1.5 w-full h-12 rounded-xl bg-white border border-brand-line px-3 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
